import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { FiMessageSquare, FiSearch, FiRefreshCcw, FiDownload } from 'react-icons/fi'
import { fetchEnquiries } from '../../store/slices/adminSlice'
import adminApi from '../../api/adminApi'
import Table from '../../components/Table/Table'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Select from '../../components/Select/Select'
import Button from '../../components/Button/Button'
import Modal from '../../components/Modal/Modal'
import './Enquiries.css'

const Enquiries = () => {
  const dispatch = useDispatch()
  const { enquiries, isLoading, error } = useSelector((state) => state.admin)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [showExportModal, setShowExportModal] = useState(false)

  useEffect(() => {
    loadEnquiries()
  }, [page, statusFilter])

  const loadEnquiries = async () => {
    const params = {
      page,
      limit: 25,
    }
    if (statusFilter !== 'all') {
      params.status = statusFilter
    }
    dispatch(fetchEnquiries(params))
  }

  const handleStatusChange = async (enquiryId, newStatus) => {
    try {
      await adminApi.patch(`/enquiries/${enquiryId}`, { status: newStatus })
      loadEnquiries()
    } catch (err) {
      console.error('Status update failed:', err)
    }
  }

  const filteredEnquiries = useMemo(() => {
    const enquiriesList = enquiries?.enquiries || enquiries?.data || []
    if (!Array.isArray(enquiriesList)) return []
    const query = searchQuery.toLowerCase()
    return enquiriesList.filter((enquiry) => {
      const matchesSearch =
        !query ||
        [
          enquiry.name,
          enquiry.email,
          enquiry.phone,
          enquiry.message,
          enquiry.source,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      
      const matchesStatus = statusFilter === 'all' || enquiry.status === statusFilter
      const matchesPriority = priorityFilter === 'all' || enquiry.priority === priorityFilter
      const matchesSource = sourceFilter === 'all' || enquiry.source === sourceFilter
      
      return matchesSearch && matchesStatus && matchesPriority && matchesSource
    })
  }, [enquiries, searchQuery, statusFilter, priorityFilter, sourceFilter])

  const handleExport = async () => {
    try {
      const enquiriesList = enquiries?.enquiries || enquiries?.data || []
      const csvData = [
        ['Name', 'Email', 'Phone', 'Course', 'Status', 'Priority', 'Source', 'Assigned To', 'Date'].join(','),
        ...filteredEnquiries.map((e) =>
          [
            `"${e.name || ''}"`,
            `"${e.email || ''}"`,
            `"${e.phone || ''}"`,
            `"${e.courseInterest?.title || ''}"`,
            `"${e.status || ''}"`,
            `"${e.priority || 'medium'}"`,
            `"${e.source || 'website'}"`,
            `"${e.assignedTo ? `${e.assignedTo.firstName} ${e.assignedTo.lastName}` : 'Unassigned'}"`,
            `"${e.createdAt ? new Date(e.createdAt).toLocaleDateString() : ''}"`,
          ].join(',')
        ),
      ].join('\n')

      const blob = new Blob([csvData], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `enquiries-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      setShowExportModal(false)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const columns = [
    {
      key: 'name',
      label: 'Contact',
      render: (row) => (
        <div>
          <strong>{row.name}</strong>
          <span className="enquiry-email">{row.email}</span>
          {row.phone && <span className="enquiry-phone">{row.phone}</span>}
        </div>
      ),
    },
    {
      key: 'message',
      label: 'Message',
      render: (row) => (
        <div className="enquiry-message">
          {row.message?.substring(0, 100)}
          {row.message?.length > 100 && '...'}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status || 'new'} />,
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (row) => {
        const priority = row.priority || 'medium'
        return (
          <span className={`priority-badge priority-${priority}`}>
            {priority.charAt(0).toUpperCase() + priority.slice(1)}
          </span>
        )
      },
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      render: (row) => {
        if (!row.assignedTo) return <span className="text-muted">Unassigned</span>
        return (
          <span>
            {row.assignedTo.firstName} {row.assignedTo.lastName}
          </span>
        )
      },
    },
    {
      key: 'source',
      label: 'Source',
      render: (row) => row.source || 'website',
    },
    {
      key: 'createdAt',
      label: 'Date',
      render: (row) => {
        if (!row.createdAt) return '—'
        return new Date(row.createdAt).toLocaleDateString()
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Link to={`/enquiries/${row._id}`} className="action-link">
          View Details
        </Link>
      ),
    },
  ]

  return (
    <div className="enquiries-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Enquiry Management</p>
          <h1>All Enquiries</h1>
          <p className="subhead">Manage leads and customer enquiries</p>
        </div>
        <div className="header-actions">
          <Button
            variant="secondary"
            onClick={() => setShowExportModal(true)}
          >
            <FiDownload size={18} />
            Export
          </Button>
          <Button
            variant="secondary"
            onClick={loadEnquiries}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </Button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search enquiries"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'new', label: 'New' },
            { value: 'contacted', label: 'Contacted' },
            { value: 'in-progress', label: 'In Progress' },
            { value: 'converted', label: 'Converted' },
            { value: 'closed', label: 'Closed' },
          ]}
        />
        <Select
          value={priorityFilter}
          onChange={(e) => {
            setPriorityFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Priority' },
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
            { value: 'urgent', label: 'Urgent' },
          ]}
        />
        <Select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Sources' },
            { value: 'website', label: 'Website' },
            { value: 'referral', label: 'Referral' },
            { value: 'social-media', label: 'Social Media' },
            { value: 'phone', label: 'Phone' },
            { value: 'email', label: 'Email' },
            { value: 'other', label: 'Other' },
          ]}
        />
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredEnquiries}
        loading={isLoading}
        emptyMessage="No enquiries found"
      />

      {/* Export Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Enquiries"
        size="small"
      >
        <div className="export-options">
          <p>Export {filteredEnquiries.length} enquiry(ies) to CSV file?</p>
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={() => setShowExportModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleExport}>
              <FiDownload size={18} />
              Export CSV
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Enquiries

